import { FC } from 'react';
import { SummaryStatCard } from 'components/summary-stat-card/summary-stat-card';
import { formatNumber } from 'utils/number';
import type { CrmTractAggregations } from 'types/api-responses';
import {
  VESTING_TILES,
  isVestingTileActive,
  vestingTileValue,
  type VestingTileConfig,
} from './utils';
import styles from './crm-tracts-summary-cards.module.scss';

interface CrmTractsSummaryCardsProps {
  aggregations: CrmTractAggregations | undefined;
  /** The statuses currently filtering the table, so the matching tile offers to clear instead. */
  activeStatuses: string[] | undefined;
  onCardClick: (tile: VestingTileConfig) => void;
  /** Whether the table is narrowed to tracts holding external title support. */
  hasExternalTitleSupport: boolean;
  onHasExternalTitleSupportChange: (checked: boolean) => void;
}

interface CardProps {
  title: string;
  value: number | undefined;
  tooltipText: string;
  applied: boolean;
  onFilterClick: () => void;
}

/**
 * One card. formatNumber's last argument is the fallback: undefined renders '--', zero renders 0.
 * @param {CardProps} props - Title, count, tooltip, applied state and click handler.
 * @return {JSX.Element} The card cell.
 */
const Card: FC<CardProps> = ({
  title,
  value,
  tooltipText,
  applied,
  onFilterClick,
}) => (
  <div className={styles.cardCell}>
    <SummaryStatCard
      title={title}
      value={formatNumber(value, 0, 0, 'decimal', true, '--')}
      tooltipText={tooltipText}
      onFilterClick={onFilterClick}
      // SummaryStatCard has no selected state and fourteen pages share it, so the control's
      // accessible name is what tells a screen reader — and these tests — which card is applied.
      filterTooltip={applied ? `Clear ${title} filter` : `Filter by ${title}`}
    />
  </div>
);

/**
 * The CRM Tract Library's card row. Clicking a card filters the table to it; clicking it again
 * clears. Every count comes from an aggregation that excludes its own filter.
 * @param {CrmTractsSummaryCardsProps} props - Counts, the active filters, and the click handlers.
 * @return {JSX.Element} The card row.
 */
const CrmTractsSummaryCards: FC<CrmTractsSummaryCardsProps> = ({
  aggregations,
  activeStatuses,
  onCardClick,
  hasExternalTitleSupport,
  onHasExternalTitleSupportChange,
}) => (
  <div className={styles.cardsContainer}>
    {VESTING_TILES.map((tile) => (
      <Card
        key={tile.title}
        title={tile.title}
        value={vestingTileValue(tile, aggregations)}
        tooltipText={tile.tooltipText}
        applied={isVestingTileActive(tile, activeStatuses)}
        onFilterClick={() => onCardClick(tile)}
      />
    ))}

    <Card
      title="Tracts with External Title Support"
      value={aggregations?.externalTitleSupport}
      tooltipText="Tracts named by at least one closing-report workbook, whether it was discovered or linked by hand"
      applied={hasExternalTitleSupport}
      onFilterClick={() =>
        onHasExternalTitleSupportChange(!hasExternalTitleSupport)
      }
    />
  </div>
);

export { CrmTractsSummaryCards };
